import { useCallback, useEffect, useRef, useState } from "react";
import { fetchImmediateSendFailures, sendMailMessage } from "@/lib/api/mailActions";
import {
  completeMailActivity,
  failMailActivity,
  mailActivitySendLabel,
  startMailActivity,
} from "@/lib/mail/mailActivityJournalNote";
import { showMailToast } from "@/lib/ui/mailToast";
import { setOutboxSendInFlight } from "@/lib/outbox/outboxSendLock";
import {
  activeOutboxItems,
  enqueueOutboxItem,
  hydrateOutboxQueue,
  mergeFailedOutboxItems,
  outboxPersistSettled,
  readOutboxQueue,
  removeOutboxItem,
  retryOutboxItem,
  updateOutboxItem,
  type OutboxEnqueuePayload,
  type OutboxEnqueueResult,
  type OutboxQueueItem,
  type OutboxServerAck,
} from "@/lib/outbox/outboxQueue";
import type { MailMessageSummary } from "@/lib/types/mail";

type Options = {
  accountId?: number;
  onDelivered?: (sentFolder?: string | null) => void;
  resolveSentFolder?: (accountId: number) => string | null | undefined;
};

export function outboxItemsToSummaries(
  items: OutboxQueueItem[],
  accountEmail: string
): MailMessageSummary[] {
  return items.map((item, index) => ({
    uid: -(index + 1),
    outboxId: item.id,
    subject: item.subject.trim() || "(Sans objet)",
    from: accountEmail,
    date: new Date(item.createdAt).toUTCString(),
    seen: true,
    flagged: false,
    hasAttachments: (item.attachments?.length ?? 0) > 0,
    size: item.bodyText.length,
    sendStatus: item.status,
    sendError: item.error,
    sendTo: item.to,
  }));
}

export function useMailSendQueue({ accountId, onDelivered, resolveSentFolder }: Options) {
  const onDeliveredRef = useRef(onDelivered);
  onDeliveredRef.current = onDelivered;
  const resolveSentFolderRef = useRef(resolveSentFolder);
  resolveSentFolderRef.current = resolveSentFolder;
  const [items, setItems] = useState<OutboxQueueItem[]>(() => readOutboxQueue());
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void hydrateOutboxQueue()
      .catch(() => undefined)
      .then(() => {
        if (cancelled) return;
        setItems(readOutboxQueue());
        setHydrated(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const processingRef = useRef(false);
  const ackWaitersRef = useRef(new Map<string, (ack: OutboxServerAck) => void>());

  const settleAck = useCallback((id: string, ack: OutboxServerAck) => {
    const waiter = ackWaitersRef.current.get(id);
    if (!waiter) return;
    ackWaitersRef.current.delete(id);
    waiter(ack);
  }, []);

  const syncFromStorage = useCallback(() => {
    setItems(readOutboxQueue());
  }, []);

  const enqueue = useCallback((payload: OutboxEnqueuePayload): OutboxEnqueueResult => {
    const result = enqueueOutboxItem(payload);
    setItems(readOutboxQueue());
    if (result.ok) {
      result.accepted = new Promise((resolve) => {
        ackWaitersRef.current.set(result.item.id, resolve);
      });
    }
    if (!result.ok) {
      const journalId = startMailActivity(
        mailActivitySendLabel({
          prefix: "Envoi",
          subject: payload.subject,
          from: payload.fromEmail,
          to: payload.to,
          cc: payload.cc,
        }),
        "send",
        "local"
      );
      failMailActivity(journalId, result.error);
      showMailToast(result.error, "error", 9000);
    }
    return result;
  }, []);

  const retry = useCallback((id: string) => {
    setItems(retryOutboxItem(id));
  }, []);

  const dismiss = useCallback((id: string) => {
    setItems(removeOutboxItem(id));
  }, []);

  const activeForAccount = activeOutboxItems(items, accountId);
  const queueCount = activeForAccount.length;
  /** pending + sending — file active (journal ; plus de spinner nav). */
  const sendingCount = activeForAccount.filter(
    (i) => i.status === "sending" || i.status === "pending"
  ).length;
  const failedCount = activeForAccount.filter((i) => i.status === "failed").length;


  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await fetchImmediateSendFailures();
      if (cancelled || !res.ok) return;
      const failed = (res.data.items ?? [])
        .filter((item) => item.clientKey && item.accountId > 0)
        .map((item) => ({
          id: item.clientKey,
          accountId: item.accountId,
          to: item.to,
          cc: item.cc,
          bcc: item.bcc,
          subject: item.subject,
          bodyText: item.bodyText,
          bodyHtml: item.bodyHtml,
          attachments: item.attachments,
          inReplyTo: item.inReplyTo,
          references: item.references,
          sentFolder: item.sentFolder,
          status: "failed" as const,
          error: item.error || "L’envoi n’a pas abouti.",
          createdAt: Date.now(),
        }));
      if (failed.length === 0) return;
      setItems(mergeFailedOutboxItems(failed));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!hydrated || processingRef.current) return;

    let cancelled = false;
    void outboxPersistSettled().then(() => {
      if (cancelled || processingRef.current) return;

      const next = readOutboxQueue().find((item) => item.status === "pending");
      if (!next) return;

      processingRef.current = true;
    setOutboxSendInFlight(true);
    setItems(updateOutboxItem(next.id, { status: "sending" }));

    const journalId = startMailActivity(
      mailActivitySendLabel({
        prefix: "Envoi",
        subject: next.subject,
        from: next.fromEmail,
        to: next.to,
        cc: next.cc,
      }),
      "send",
      "local"
    );

    void sendMailMessage({
      accountId: next.accountId,
      clientKey: next.id,
      sentFolder: resolveSentFolderRef.current?.(next.accountId) ?? undefined,
      to: next.to,
      cc: next.cc,
      bcc: next.bcc,
      subject: next.subject,
      bodyText: next.bodyText,
      bodyHtml: next.bodyHtml,
      attachments: next.attachments,
      inReplyTo: next.inReplyTo,
      references: next.references,
    })
      .then((res) => {
        if (res.ok) {
          setItems(removeOutboxItem(next.id));
          completeMailActivity(
            journalId,
            mailActivitySendLabel({
              prefix: "Envoi pris",
              subject: next.subject,
              from: next.fromEmail,
              to: next.to,
              cc: next.cc,
            })
          );
          settleAck(next.id, { ok: true });
          onDeliveredRef.current?.(res.data.sentFolder);
        } else {
          setItems(
            updateOutboxItem(next.id, {
              status: "failed",
              error: res.message,
            })
          );
          failMailActivity(journalId, res.message);
          settleAck(next.id, { ok: false, error: res.message });
        }
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : "Erreur réseau";
        setItems(updateOutboxItem(next.id, { status: "failed", error: message }));
        failMailActivity(journalId, message);
        settleAck(next.id, { ok: false, error: message });
      })
      .finally(() => {
        processingRef.current = false;
        setOutboxSendInFlight(false);
        syncFromStorage();
      });
    });
    return () => {
      cancelled = true;
    };
  }, [items, hydrated, settleAck, syncFromStorage]);

  const getItemById = useCallback(
    (id: string | null | undefined) =>
      id ? activeForAccount.find((item) => item.id === id) ?? null : null,
    [activeForAccount]
  );

  const toSummaries = useCallback(
    (accountEmail: string) => outboxItemsToSummaries(activeForAccount, accountEmail),
    [activeForAccount]
  );

  return {
    activeItems: activeForAccount,
    queueCount,
    sendingCount,
    failedCount,
    enqueue,
    retry,
    dismiss,
    getItemById,
    toSummaries,
  };
}
